import { useEffect, useState } from 'react';
import type { ScanDto, ValuationResponse, WatchlistItem } from '@compflash/contracts';
import { useSession } from '../lib/session';
import { describeCondition, describeIdentity, formatDateTime, formatMoney } from '../lib/format';
import { ResultPanel } from '../components/ResultPanel';

export function HistoryPage(): JSX.Element {
  const { client } = useSession();
  const [scans, setScans] = useState<ScanDto[]>([]);
  const [selected, setSelected] = useState<ScanDto | null>(null);
  const [savedOnly, setSavedOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = query.trim()
          ? await client.searchScans(query.trim())
          : await client.listScans({ limit: 50, saved: savedOnly });
        setScans('results' in result ? result.results : result.scans);
      } catch (caught) {
        setError((caught as Error).message);
      }
    })();
  }, [client, savedOnly, query]);

  return (
    <div className="two-column">
      <section className="panel">
        <h2>Scan history</h2>
        <div className="row">
          <input placeholder="Search your scans" value={query} onChange={(event) => setQuery(event.target.value)} />
          <label className="inline">
            <input type="checkbox" checked={savedOnly} onChange={(event) => setSavedOnly(event.target.checked)} /> Saved only
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        <ul className="list">
          {scans.map((scan) => (
            <li key={scan.id}>
              <button type="button" className="list-item" onClick={() => setSelected(scan)}>
                <span className="list-title">{describeIdentity(scan.identification?.best?.identity as never)}</span>
                <span className="muted small">
                  {formatDateTime(scan.createdAt)} · {scan.recommendation?.verdict.replace(/_/g, ' ') ?? scan.stage}
                  {scan.recommendation?.maxBid ? ` · max ${formatMoney(scan.recommendation.maxBid)}` : ''}
                </span>
              </button>
              <div className="row">
                <button type="button" className="ghost small" onClick={() => void client.setScanSaved(scan.id, !scan.saved)}>
                  {scan.saved ? 'Unsave' : 'Save'}
                </button>
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => {
                    void client.deleteScan(scan.id);
                    setScans((current) => current.filter((entry) => entry.id !== scan.id));
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
          {scans.length === 0 && <li className="muted">No scans yet.</li>}
        </ul>
      </section>

      <div>
        {selected?.identification ? (
          <ResultPanel
            identity={(selected.identification.best?.identity ?? null) as Record<string, unknown> | null}
            condition={selected.identification.condition as never}
            identityConfidence={selected.identification.best?.confidence ?? null}
            abstained={selected.identification.abstained}
            abstentionReason={selected.identification.abstentionReason}
            valuation={selected.valuation}
            recommendation={selected.recommendation}
            timings={selected.timings}
          />
        ) : (
          <div className="panel">
            <p className="muted">Select a scan to see its full result.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function LookupPage(): JSX.Element {
  const { client } = useSession();
  const [form, setForm] = useState({
    subject: 'Victor Wembanyama',
    year: '2023',
    manufacturer: 'Panini',
    setName: 'Prizm',
    cardNumber: '136',
    parallel: 'Silver',
    gradeCompany: 'PSA',
    grade: '10',
    raw: false,
  });
  const [result, setResult] = useState<ValuationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await client.valuate({
        identity: {
          subject: form.subject || null,
          year: form.year ? Number(form.year) : null,
          manufacturer: form.manufacturer || null,
          setName: form.setName || null,
          cardNumber: form.cardNumber || null,
          parallel: form.parallel || null,
        },
        condition: form.raw
          ? { kind: 'raw', estimate: 'near_mint', notes: null }
          : {
              kind: 'graded',
              grade: {
                company: form.gradeCompany as 'PSA',
                grade: Number(form.grade),
                label: null,
                qualifier: null,
                certNumber: null,
                subgrades: null,
                isAuthenticOnly: false,
                autoGrade: null,
              },
            },
        currency: 'USD',
      });
      setResult(response);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const update = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <div className="two-column">
      <form className="panel" onSubmit={(event) => void submit(event)}>
        <h2>Manual lookup</h2>
        <p className="muted small">
          Value a card without scanning. Uses the same comps, pricing and recommendation engines as a live scan.
        </p>
        <div className="field-grid">
          <label className="field"><span>Player / subject</span><input value={form.subject} onChange={update('subject')} /></label>
          <label className="field"><span>Year</span><input value={form.year} onChange={update('year')} /></label>
          <label className="field"><span>Manufacturer</span><input value={form.manufacturer} onChange={update('manufacturer')} /></label>
          <label className="field"><span>Set</span><input value={form.setName} onChange={update('setName')} /></label>
          <label className="field"><span>Card number</span><input value={form.cardNumber} onChange={update('cardNumber')} /></label>
          <label className="field"><span>Parallel</span><input value={form.parallel} onChange={update('parallel')} /></label>
          <label className="field">
            <span>Grader</span>
            <select value={form.gradeCompany} onChange={update('gradeCompany')} disabled={form.raw}>
              {['PSA', 'BGS', 'SGC', 'CGC'].map((company) => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          </label>
          <label className="field"><span>Grade</span><input value={form.grade} onChange={update('grade')} disabled={form.raw} /></label>
          <label className="inline">
            <input type="checkbox" checked={form.raw} onChange={(event) => setForm((c) => ({ ...c, raw: event.target.checked }))} /> Raw card
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy}>{busy ? 'Valuing…' : 'Value this card'}</button>
      </form>

      <div>
        {result ? (
          <ResultPanel
            identity={result.identity as unknown as Record<string, unknown>}
            condition={result.condition as never}
            identityConfidence={1}
            abstained={false}
            abstentionReason={null}
            valuation={result.valuation as never}
            recommendation={result.recommendation as never}
            onWatch={() =>
              void client.addToWatchlist({ identity: result.identity, condition: result.condition, note: null })
            }
          />
        ) : (
          <div className="panel"><p className="muted">Fill in what you know and submit. Unknown fields are allowed.</p></div>
        )}
      </div>
    </div>
  );
}

export function WatchlistPage(): JSX.Element {
  const { client } = useSession();
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    try {
      setItems((await client.watchlist()).items);
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  useEffect(() => {
    void load();
  }, [client]);

  return (
    <section className="panel">
      <h2>Watchlist</h2>
      <p className="muted small">Cards you are tracking. Add them from a lookup or a scan result.</p>
      {error && <p className="error">{error}</p>}
      <table className="table">
        <thead>
          <tr><th>Card</th><th>Condition</th><th className="num">Target</th><th>Note</th><th /></tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>{describeIdentity(item.identity as unknown as Record<string, unknown>)}</td>
              <td>{describeCondition(item.condition as never)}</td>
              <td className="num">{formatMoney(item.targetPrice)}</td>
              <td>{item.note ?? '—'}</td>
              <td>
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => {
                    void client.removeFromWatchlist(item.id).then(load);
                  }}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={5} className="muted">Nothing on the watchlist yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
